import React, { useRef, useState } from 'react'
import {
  Box,
  Button,
  Text,
  Heading,
  SimpleGrid,
  Stack,
  Flex,
  Badge,
  Icon,
  Container,
  VStack,
  HStack,
  chakra,
  shouldForwardProp,
  Divider
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { motion, useScroll, useTransform, useSpring } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { FaRobot, FaShieldAlt, FaBolt, FaBrain, FaChartLine, FaGem, FaCheckCircle, FaRocket } from 'react-icons/fa'

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => shouldForwardProp(prop) || prop === 'transition',
})

// --- Animations ---
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 10px rgba(245, 158, 11, 0.2); }
  50% { box-shadow: 0 0 25px rgba(245, 158, 11, 0.5); }
  100% { box-shadow: 0 0 10px rgba(245, 158, 11, 0.2); }
`
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`
const scrollTicker = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

// --- Components ---

const Particles = () => (
  <Box position="absolute" inset={0} overflow="hidden" pointerEvents="none" zIndex={0}>
     {[...Array(15)].map((_, i) => (
       <Box
         key={i}
         position="absolute"
         top={`${Math.random() * 100}%`}
         left={`${Math.random() * 100}%`}
         w={`${Math.random() * 3 + 1}px`}
         h={`${Math.random() * 3 + 1}px`}
         bg="brand.400"
         borderRadius="full"
         opacity={Math.random() * 0.4 + 0.1}
         animation={`${float} ${Math.random() * 5 + 5}s infinite ease-in-out`}
         sx={{ animationDelay: `${Math.random() * 5}s` }}
       />
     ))}
  </Box>
)

const Spacer = ({ h = 20 }: { h?: number }) => <Box h={h} />

const SectionTitle = ({ title, subtitle }: { title: string, subtitle?: string }) => (
  <VStack spacing={4} mb={16} textAlign="center">
    <Badge colorScheme="orange" variant="outline" px={3} py={1} borderRadius="full">
      {subtitle}
    </Badge>
    <Heading as="h2" size="2xl" color="white">
      {title}
    </Heading>
    <Box w="60px" h="4px" bg="brand.500" borderRadius="full" />
  </VStack>
)

const StatDisplay = ({ label, value, sub }: any) => (
  <VStack align="start" spacing={0}>
    <Text color="gray.500" fontSize="sm" fontWeight="medium">{label}</Text>
    <Heading size="lg" color="white" my={1}
      bgGradient="linear(to-br, white, gray.400)"
      bgClip="text"
    >
      {value}
    </Heading>
    <Text color="brand.400" fontSize="xs" fontWeight="bold">{sub}</Text>
  </VStack>
)

const TickerTape = () => {
  const items = ["AI TRADING SYSTEM", "AUTOMATED EXECUTION", "REAL-TIME MONITORING", "RISK MANAGEMENT", "24/7 MARKET SCANNING", "CLOUD-BASED SECURITY"]
  return (
    <Box w="full" bg="whiteAlpha.50" borderY="1px solid" borderColor="whiteAlpha.100" py={3} overflow="hidden" position="relative">
      <Box whiteSpace="nowrap" display="flex" animation={`${scrollTicker} 20s linear infinite`}>
        {[...items, ...items, ...items, ...items].map((text, i) => (
          <Text key={i} color="gray.400" fontSize="sm" fontWeight="bold" mx={8} display="inline-block" letterSpacing="widest">
             <Icon as={FaGem} color="brand.500" mr={2} w={3} h={3} mb="2px"/>
             {text}
          </Text>
        ))}
      </Box>
    </Box>
  )
}



const AnimatedBorderCard = ({ children, delay = 0 }: any) => {
  return (
    <Box
      position="relative"
      p="2px"
      borderRadius="3xl"
      overflow="hidden"
      role="group"
      // @ts-ignore
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      // @ts-ignore
      transition={{ duration: 0.5, delay }}
    >
      {/* Animated Border */}
      <Box
        position="absolute"
        inset="-50%"
        bgGradient="conic-gradient(from 0deg, transparent 0deg, brand.500 90deg, transparent 180deg, brand.500 270deg, transparent 360deg)"
        animation="spin 4s linear infinite"
        opacity={0}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.3s"
      />
      
      {/* Content Container */}
      <Box
        position="relative"
        bg="rgba(10, 10, 15, 0.9)"
        backdropFilter="blur(20px)"
        borderRadius="3xl"
        h="full"
        p={8}
        zIndex={1}
        _groupHover={{ bg: "rgba(10, 10, 15, 0.95)" }}
        transition="background 0.3s"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        {children}
      </Box>
    </Box>
  )
}

const Home = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  
  return (
    <DefaultLayout>
      <Box bg="#050505" minH="100vh" position="relative" overflowX="hidden">
        
        {/* Global Background Elements */}
        <Box position="fixed" inset={0} bgImage="radial-gradient(circle at 50% 0%, #1a1500 0%, #050505 60%)" zIndex={0} pointerEvents="none" />
        <Box position="fixed" inset={0} opacity={0.4} bgImage="url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')" zIndex={0} pointerEvents="none" />
        <Particles />

        {/* ==================== SECTION 1: HERO ==================== */}
        <Box as="section" minH="90vh" display="flex" flexDirection="column" justifyContent="center" position="relative" zIndex={1} pt={20}>
           <Container maxW="7xl" textAlign="center">
{/* @ts-ignore */}
              <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <Badge mb={6} px={4} py={2} borderRadius="full" colorScheme="orange" variant="outline" fontSize="xs" letterSpacing="widest">
                  AI-POWERED TRADING PLATFORM
                </Badge>
                
                <Heading as="h1" fontSize={{ base: '15vw', md: '160px' }} fontWeight="900" lineHeight="0.85" letterSpacing="-0.06em" color="white" mb={2}>
                  WINNING
                </Heading>
                <Heading as="h1" fontSize={{ base: '15vw', md: '160px' }} fontWeight="900" lineHeight="0.85" letterSpacing="-0.06em" color="transparent" sx={{ WebkitTextStroke: '2px #F59E0B' }}>
                  BOT
                </Heading>
              </MotionBox>
              
{/* @ts-ignore */}
              <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }}>
                <Text fontSize="xl" color="gray.400" mt={8} maxW="2xl" mx="auto">
                  {t('감정 없는 알고리즘이 24시간 당신의 자산을 불려줍니다.')}
                </Text>
                
                <HStack spacing={6} justify="center" mt={10}>
                  <Button size="lg" h="60px" px={10} borderRadius="full" bg="brand.500" color="#1a1a1a" _hover={{ bg: 'brand.400', transform: 'scale(1.05)' }} onClick={() => auth.id ? navigate('/trading') : navigate('/signIn')}>
                    {t('시작하기')}
                  </Button>
                  <Button size="lg" h="60px" px={10} borderRadius="full" variant="outline" color="white" _hover={{ bg: 'whiteAlpha.100' }} onClick={() => navigate('/intro')}>
                    {t('더 알아보기')}
                  </Button>
                </HStack>
              </MotionBox>
           </Container>
           
           <Box mt="auto" w="full">
             <TickerTape />
           </Box>
        </Box>


        {/* ==================== SECTION 2: LIVE PERFORMANCE ==================== */}
        <Box as="section" py={24} position="relative" zIndex={1} bg="linear-gradient(to bottom, transparent, rgba(0,0,0,0.8))">
          <Container maxW="7xl">
{/* @ts-ignore */}
            <MotionBox initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <SectionTitle title={t("Live Performance")} subtitle="REAL-TIME DATA" />
              
              <Box borderRadius="3xl" bg="dark.card" border="1px solid" borderColor="whiteAlpha.100" p={{ base: 6, md: 12 }} position="relative" overflow="hidden">
                 {/* Decorative background grid in chart area */}
                 <Box position="absolute" inset={0} bgImage="linear-gradient(rgba(245, 158, 11, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(245, 158, 11, 0.03) 1px, transparent 1px)" bgSize="40px 40px" />
                 
                 <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={12} position="relative" zIndex={2}>
                                        {/* Stat Numbers */}
                     <VStack align="start" spacing={8} justify="center">
                        <Box>
                          <Text color="gray.500" fontSize="sm" mb={1}>SYSTEM STATUS</Text>
                          <Heading size="2xl" color="brand.500">OPTIMIZED</Heading>
                          <Badge colorScheme="green" mt={2}>ACTIVE MONITORING</Badge>
                        </Box>
                        <Divider borderColor="whiteAlpha.200" />
                        <Box>
                          <Text color="gray.500" fontSize="sm" mb={1}>STRATEGY ENGINE</Text>
                          <Heading size="xl" color="white">AI DRIVEN</Heading>
                          <Text fontSize="sm" color="gray.400">Multi-Factor Analysis</Text>
                        </Box>
                     </VStack>

                    {/* Animated Chart Simulation */}
                    <Box gridColumn={{ lg: "span 2" }} h="300px" display="flex" alignItems="end" justifyContent="space-between" gap={2}>
                       {[...Array(20)].map((_, i) => {
                          const h = Math.random() * 60 + 20; // 20% to 80%
                              return (
                            <MotionBox 
                              key={i} 
                              flex={1} 
                              bgGradient="linear(to-t, brand.500, brand.900)" 
                              borderRadius="sm"
                              opacity={0.8}
                              initial={{ height: "10%" }}
                              whileInView={{ height: `${h}%` }}
                        // @ts-ignore
                              transition={{ duration: 1.5, delay: i * 0.05 }}
                              _hover={{ height: "100%", bg: "brand.300", opacity: 1 }}
                            />
                          )
                       })}
                    </Box>

                 </SimpleGrid>
              </Box>
            </MotionBox>
          </Container>
        </Box>


        {/* ==================== SECTION 3: FEATURES (PREMIUM CARDS) ==================== */}
        <Box as="section" py={24} position="relative" zIndex={1}>
          <Container maxW="7xl">
            <SectionTitle title={t("Core Features")} subtitle="WHY WINNINGBOT" />

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              
              {/* Feature 1: AI Engine */}
              <AnimatedBorderCard delay={0}>
                  <VStack align="start" spacing={6} h="full" justify="space-between">
                     <Box>
                       <Flex w={14} h={14} borderRadius="2xl" bg="brand.500" align="center" justify="center" mb={6} boxShadow="0 0 20px rgba(245, 158, 11, 0.4)">
                         <Icon as={FaBrain} w={8} h={8} color="white" />
                       </Flex>
                       <Heading size="lg" color="white" mb={4}>Deep Learning Engine</Heading>
                       <Text color="gray.400" lineHeight="tall">
                         {t('수백만 개의 시장 데이터를 실시간으로 분석하여 최적의 진입 시점을 찾아냅니다.')}
                       </Text>
                     </Box>
                     <Text fontSize="sm" fontWeight="bold" color="brand.400" display="flex" alignItems="center">
                       LEARN MORE <Icon as={ArrowForwardIcon} ml={2} />
                     </Text>
                  </VStack>
              </AnimatedBorderCard>

              {/* Feature 2: Speed & Precision */}
              <AnimatedBorderCard delay={0.2}>
                  <VStack align="start" spacing={6} h="full" justify="space-between">
                     <Box>
                       <Flex w={14} h={14} borderRadius="2xl" bg="blue.500" align="center" justify="center" mb={6} boxShadow="0 0 20px rgba(59, 130, 246, 0.4)">
                         <Icon as={FaBolt} w={8} h={8} color="white" />
                       </Flex>
                       <Heading size="lg" color="white" mb={4}>Microsecond Execution</Heading>
                       <Text color="gray.400" lineHeight="tall">
                         {t('인간의 반응속도를 뛰어넘는 0.001초 단위의 초고속 주문 실행으로 슬리피지를 최소화합니다.')}
                       </Text>
                     </Box>
                     <HStack spacing={4} w="full" pt={4} borderTop="1px solid" borderColor="whiteAlpha.100">
                        <VStack align="start" spacing={0}>
                          <Text fontSize="xs" color="gray.500">LATENCY</Text>
                          <Text fontSize="lg" fontWeight="bold" color="white">1ms</Text>
                        </VStack>
                        <Divider orientation="vertical" h="30px" />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="xs" color="gray.500">UPTIME</Text>
                          <Text fontSize="lg" fontWeight="bold" color="white">99.9%</Text>
                        </VStack>
                     </HStack>
                  </VStack>
              </AnimatedBorderCard>

              {/* Feature 3: Security */}
              <AnimatedBorderCard delay={0.4}>
                  <VStack align="start" spacing={6} h="full" justify="space-between">
                     <Box>
                       <Flex w={14} h={14} borderRadius="2xl" bg="green.500" align="center" justify="center" mb={6} boxShadow="0 0 20px rgba(34, 197, 94, 0.4)">
                         <Icon as={FaShieldAlt} w={8} h={8} color="white" />
                       </Flex>
                       <Heading size="lg" color="white" mb={4}>Bank-Grade Security</Heading>
                       <Text color="gray.400" lineHeight="tall">
                         {t('API 키 안전 저장 및 IP 화이트리스트 관리를 통해 고객님의 자산을 안전하게 보호합니다.')}
                       </Text>
                     </Box>
                     <Flex wrap="wrap" gap={2}>
                        <Badge colorScheme="green" variant="solid" borderRadius="md">AES-256</Badge>
                        <Badge colorScheme="green" variant="secondary" borderRadius="md">AWS KMS</Badge>
                     </Flex>
                  </VStack>
              </AnimatedBorderCard>

            </SimpleGrid>

            {/* Sub Features Grid */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mt={16}>
               {[
                 { icon: FaChartLine, label: "Advanced Charting" },
                 { icon: FaRobot, label: "Automated DCA" },
                 { icon: FaGem, label: "Premium Signals" },
                 { icon: FaCheckCircle, label: "Backtesting" }
               ].map((item, idx) => (
                 <HStack key={idx} bg="whiteAlpha.50" p={4} borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100" justify="center"
                    _hover={{ bg: "whiteAlpha.100", borderColor: "brand.500" }} transition="all 0.2s"
                 >
                    <Icon as={item.icon} color="brand.500" boxSize={5} />
                    <Text fontWeight="bold" color="white" fontSize="sm">{item.label}</Text>
                 </HStack>
               ))}
            </SimpleGrid>

          </Container>
        </Box>


        {/* ==================== SECTION 4: CTA ==================== */}
        <Box as="section" py={32} position="relative" zIndex={1} textAlign="center">
           <Container maxW="4xl">
{/* @ts-ignore */}
              <MotionBox initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                <Heading size="3xl" color="white" mb={6}>
                   Start Winning Today
                </Heading>
                <Text fontSize="xl" color="gray.400" mb={12}>
                   {t('지금 바로 WinningBot에 가입하고, 당신의 투자를 혁신하세요.')}
                </Text>
                
                <Flex justify="center" gap={6} flexDirection={{ base: 'column', sm: 'row' }}>
                   <Button size="lg" h="70px" px={12} fontSize="xl" borderRadius="full" bgGradient="linear(to-r, brand.500, brand.400)" color="#1a1a1a" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                     onClick={() => auth.id ? navigate('/trading') : navigate('/signIn')}
                     rightIcon={<FaRocket />}
                   >
                     {t('무료로 시작하기')}
                   </Button>
                </Flex>

                <HStack justify="center" mt={12} spacing={8} color="gray.500">
                   <Flex align="center"><Icon as={FaCheckCircle} mr={2} color="brand.500" /> No Credit Card</Flex>
                   <Flex align="center"><Icon as={FaCheckCircle} mr={2} color="brand.500" /> Cancel Anytime</Flex>
                   <Flex align="center"><Icon as={FaCheckCircle} mr={2} color="brand.500" /> 24/7 Support</Flex>
                </HStack>
              </MotionBox>
           </Container>
        </Box>

      </Box>
    </DefaultLayout>
  )
}

export default Home
